import { useSetAtom, useAtomValue } from 'jotai';
import { toast } from 'sonner';
import { useState } from 'react';

import { Bot, Lightbulb, MessageSquare, UserPlus } from 'lucide-react';

import { AssignmentResponse } from '@/types';
import { isContentGenerated } from '@/utils/assignments/isContentGenerated';
import {
  assignmentsLoadingAtom,
  generateEditorialAngleActionAtom,
  generateAvatarBriefingActionAtom,
  generateInterviewScriptActionAtom,
  setEditorialAngleActionAtom,
  fetchAssignmentActionAtom,
  updateAssignmentActionAtom,
  createInterviewerPersonaAtom,
} from '@/stores/assignmentsStore';

import { WorkflowButton } from './WorkflowButton';
import { InterviewLink } from './InterviewLink';
import { Badge } from '@/components/ui/badge';
import { CreatePersonaDialog } from '@/components/CreatePersonaDialog';

export interface PreInterviewWorkflowProps {
  assignment: AssignmentResponse;
}

export const PreInterviewWorkflow: React.FC<PreInterviewWorkflowProps> = ({
  assignment,
}) => {
  const isLoading = useAtomValue(assignmentsLoadingAtom);
  const fetchAssignment = useSetAtom(fetchAssignmentActionAtom);
  const generateEditorialAngle = useSetAtom(generateEditorialAngleActionAtom);
  const setEditorialAngle = useSetAtom(setEditorialAngleActionAtom);
  const generateBriefing = useSetAtom(generateAvatarBriefingActionAtom);
  const generateScript = useSetAtom(generateInterviewScriptActionAtom);
  const createPersona = useSetAtom(createInterviewerPersonaAtom);
  const updateAssignment = useSetAtom(updateAssignmentActionAtom);

  const [isPersonaModalOpen, setIsPersonaModalOpen] = useState(false);

  const handleGenerateEditorialAngle = async (
    assignmentId: string,
    tipId?: string
  ) => {
    try {
      const generated = await generateEditorialAngle(tipId || assignmentId);
      await setEditorialAngle(assignmentId, JSON.stringify(generated));
      fetchAssignment(assignmentId);

      toast.success('Editorial angle generated successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to generate editorial angle';
      toast.error(errorMessage);
    }
  };

  const handleGenerateContent = async (
    assignmentId: string,
    type: 'briefing' | 'script'
  ) => {
    try {
      switch (type) {
        case 'briefing':
          await generateBriefing(assignmentId);
          toast.success('Avatar briefing generated successfully');
          break;
        case 'script':
          await generateScript(assignmentId);
          toast.success('Interview script generated successfully');
          break;
        default:
          toast.error('Unknown content type');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : `Failed to generate ${type}`;
      toast.error(errorMessage);
    } finally {
      fetchAssignment(assignmentId);
    }
  };

  const handleOpenPersonaModal = () => {
    setIsPersonaModalOpen(true);
  };

  const handleCreatePersona = async (replicaId?: string) => {
    await createPersona(assignment.id, replicaId);

    const interviewUrl = `${window.location.origin}/conversation/${assignment.id}`;
    await updateAssignment(assignment.id, { interviewUrl });
    fetchAssignment(assignment.id);
  };

  const preStepsCompleted = [
    assignment.editorialAngle && 'Angle',
    assignment.avatarBriefing && 'Brief',
    assignment.interviewScript && 'Script',
    assignment.interviewUrl && 'Link',
  ].filter(Boolean).length;

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <h4 className='text-sm font-medium'>Pre-Interview Workflow</h4>
        <Badge variant='outline' className='text-xs'>
          {preStepsCompleted}/4 Complete
        </Badge>
      </div>

      <div className='grid grid-cols-1 gap-2'>
        {/* Editorial Angle */}
        <WorkflowButton
          disabled={isLoading}
          onClick={() =>
            handleGenerateEditorialAngle(assignment.id, assignment.tipId)
          }
          isContentGenerated={isContentGenerated(assignment, 'angle')}
          Icon={Lightbulb}
          generatedAt={assignment.editorialAngleGeneratedAt}
          title='Editorial Angle'
        />

        {/* Avatar Briefing */}
        <WorkflowButton
          disabled={isLoading || !isContentGenerated(assignment, 'angle')}
          onClick={() => handleGenerateContent(assignment.id, 'briefing')}
          isContentGenerated={isContentGenerated(assignment, 'briefing')}
          Icon={Bot}
          generatedAt={assignment.avatarBriefingGeneratedAt}
          title='Avatar Briefing'
        />

        {/* Interview Script */}
        <WorkflowButton
          disabled={isLoading || !isContentGenerated(assignment, 'angle')}
          onClick={() => handleGenerateContent(assignment.id, 'script')}
          isContentGenerated={isContentGenerated(assignment, 'script')}
          Icon={MessageSquare}
          generatedAt={assignment.interviewScriptGeneratedAt}
          title='Interview Script'
        />

        {/* Create Persona */}
        <WorkflowButton
          onClick={handleOpenPersonaModal}
          disabled={
            isLoading ||
            !(
              assignment.editorialAngle &&
              assignment.avatarBriefing &&
              assignment.interviewScript
            )
          }
          isContentGenerated={!!assignment.interviewUrl}
          Icon={UserPlus}
          title={`${assignment.interviewUrl ? 'Interviewer Persona' : 'Create Interviewer Persona'}`}
        />

        {/* Get interview link */}
        <InterviewLink assignment={assignment} />
      </div>

      {/* Create persona modal window */}
      <CreatePersonaDialog
        open={isPersonaModalOpen}
        onOpenChange={setIsPersonaModalOpen}
        onPersonaCreated={handleCreatePersona}
      />
    </div>
  );
};
